import React, { useState, useEffect } from 'react';
import {
	Package,
	Search,
	Eye,
	CheckCircle,
	X,
	Clock,
	AlertTriangle,
	User,
	Calendar,
	Image as ImageIcon,
	ChevronLeft,
	ChevronRight,
	Star
} from 'lucide-react';
import toast from 'react-hot-toast';
import { purchaseService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';

const BranchStock = () => {
	const { user } = useAuth();
	const [stockItems, setStockItems] = useState([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [activeTab, setActiveTab] = useState('pending'); // 'pending', 'authenticated', 'rejected'
	const [showModal, setShowModal] = useState(false);
	const [selectedItem, setSelectedItem] = useState(null);
	const [overallRemark, setOverallRemark] = useState('');
	const [fullScreenImage, setFullScreenImage] = useState(null);
	const [currentImageIndex, setCurrentImageIndex] = useState(0);
	const [currentImageArray, setCurrentImageArray] = useState([]);
	const [overallEstimate, setOverallEstimate] = useState('');
	const [loading, setLoading] = useState(true);

	// Helper function to check if user is authenticator (case-insensitive)
	const isAuthenticator = () => {
		const userRole = user?.role?.toLowerCase();
		return userRole === 'authenticator';
	};

	// Authentication parameters for authenticator role
	const [authParams, setAuthParams] = useState({
		purchaseProof: 0,
		fabric: 0,
		stitching: 0,
		labels: 0,
		condition: 0
	});

	// Fetch branch stock items from API
	const fetchBranchStock = async () => {
		setLoading(true);
		try {
			const response = await purchaseService.getBranchStock();
			if (response.success) {
				setStockItems(response.data || []);
			} else {
				toast.error('Failed to fetch branch stock');
				setStockItems([]);
			}
		} catch (error) {
			console.error('Error fetching branch stock:', error);
			toast.error('Error fetching branch stock');
			setStockItems([]);
		} finally {
			setLoading(false);
		}
	};

	// Load branch stock items on component mount
	useEffect(() => {
		fetchBranchStock();
	}, []);

	const getStatusColor = (status) => {
		// Handle both numeric and string status values
		switch (status) {
			case 'pending':
			case 1: return '#f59e0b';
			case 'in-process':
			case 2: return '#6366f1';
			case 'authenticated':
			case 6: return '#3b82f6';
			case 'approved':
			case 7: return '#10b981';
			case 'rejected':
			case 8: return '#ef4444';
			default: return '#6b7280';
		}
	};

	const getStatusIcon = (status) => {
		// Handle both numeric and string status values
		switch (status) {
			case 'pending':
			case 1: return Clock;
			case 'in-process':
			case 2: return Eye;
			case 'authenticated':
			case 6: return CheckCircle;
			case 'approved':
			case 7: return CheckCircle;
			case 'rejected':
			case 8: return X;
			default: return Package;
		}
	};

	const getStatusText = (status) => {
		// Handle both numeric and string status values
		switch (status) {
			case 'pending':
			case 1: return 'Pending';
			case 'in-process':
			case 2: return 'In Process';
			case 'authenticated':
			case 6: return 'Authenticated';
			case 'approved':
			case 7: return 'Approved';
			case 'rejected':
			case 8: return 'Rejected';
			default: return 'Unknown';
		}
	};

	const isStatusPendingOrInProcess = (status) => {
		// Handle both numeric and string status values
		return status === 'pending' || status === 1 || 
		       status === 'in-process' || status === 2;
	};

	const openImageModal = (image, imageArray, index) => {
		setFullScreenImage(image);
		setCurrentImageArray(imageArray);
		setCurrentImageIndex(index);
	};

	const closeImageModal = () => {
		setFullScreenImage(null);
		setCurrentImageArray([]);
		setCurrentImageIndex(0);
	};

	const navigateImage = (direction) => {
		if (direction === 'prev') {
			setCurrentImageIndex(prev =>
				prev === 0 ? currentImageArray.length - 1 : prev - 1
			);
		} else {
			setCurrentImageIndex(prev =>
				prev === currentImageArray.length - 1 ? 0 : prev + 1
			);
		}
	};

	const openModal = (item) => {
		setSelectedItem(item);
		// Prefill authentication data if item has been authenticated before
		setAuthParams({
			purchaseProof: item?.rating_purchase_proof || item?.ratingPurchaseProof || 0,
			fabric: item?.rating_fabric || item?.ratingFabric || 0,
			stitching: item?.rating_stitching || item?.ratingStitching || 0,
			labels: item?.rating_labels || item?.ratingLabels || 0,
			condition: item?.rating_condition || item?.ratingCondition || 0
		});
		// Prefill overall remark if present
		setOverallRemark(item?.review_comment || item?.reviewComment || '');
		// Prefill overall estimate if present on item
		const estimate = item?.total_estimated_cost || item?.totalEstimatedCost || '';
		setOverallEstimate(estimate ? String(estimate) : '');
		setShowModal(true);
	};

	const closeModal = () => {
		setShowModal(false);
		setSelectedItem(null);
		setAuthParams({
			purchaseProof: 0,
			fabric: 0,
			stitching: 0,
			labels: 0,
			condition: 0
		});
		setOverallRemark('');
		setOverallEstimate('');
	};

	const handleStarClick = (param, rating) => {
		if (!isAuthenticator()) return;
		setAuthParams(prev => ({
			...prev,
			[param]: rating
		}));
	};

	const getAverageRating = () => {
		const values = Object.values(authParams);
		const nonZeroValues = values.filter(val => val > 0);
		return nonZeroValues.length > 0
			? (nonZeroValues.reduce((sum, val) => sum + val, 0) / nonZeroValues.length).toFixed(1)
			: 0;
	};

	const handleMarkAuthenticated = () => {
		if (!selectedItem) return;
		const ok = window.confirm('Are you sure you want to mark this dress as Authenticated?');
		if (!ok) return;
		submitReview(selectedItem, 'authenticate');
	};

	const handleReject = () => {
		if (!selectedItem) return;
		const ok = window.confirm('Are you sure you want to Reject this dress?');
		if (!ok) return;
		submitReview(selectedItem, 'reject');
	};

	const submitReview = async (item, action) => {
		try {
			const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
			if (!storedUser?.token || !user?.id) {
				toast.error('Your session has expired. Please log in again.');
				return;
			}
			// Overall Estimate is required for both authenticate and reject actions
			const val = overallEstimate !== '' && overallEstimate !== null ? Number(overallEstimate) : NaN;
			if (!Number.isFinite(val) || val <= 0) {
				toast.error('Overall estimate is required and must be greater than 0.');
				return;
			}
			const payload = {
				listing_id: item?.listing_id || item?.backendId || item?.id, // Use listing_id from item
				authenticatorId: user?.id,

				
				status: action === 'authenticate' ? "authenticated" : "rejected", // 6 = authenticated, 8 = rejected
				
				rating_purchase_proof: authParams.purchaseProof || null,
				rating_fabric: authParams.fabric || null,
				rating_stitching: authParams.stitching || null,
				rating_labels: authParams.labels || null,
				rating_condition: authParams.condition || null,

				average_rating: Number(getAverageRating()) || null,
				total_estimated_cost: overallEstimate !== '' ? Number(overallEstimate) : 0,
				review_comment: overallRemark || null,
			};
			const res = await purchaseService.submitAuthenticationReview(payload);
			if (res?.success) {
				toast.success(action === 'authenticate' ? 'Marked as authenticated' : 'Rejected');
				closeModal();
				// Refresh the branch stock list
				await fetchBranchStock();
			} else {
				toast.error(res?.message || 'Failed to submit review');
			}
		} catch (err) {
			toast.error(err?.message || 'Failed to submit review');
		}
	};

	// Helper function to check if item matches a tab status
	const itemMatchesTab = (item, tab) => {
		const status = item.status;
		const dressStatusId = item.dress_status_id || item.dressStatusId;
		
		switch (tab) {
			case 'pending':
				// Check both status and dress_status_id for pending/in-process items
				return (status === 'pending' || status === 1 || 
				       status === 'in-process' || status === 2) &&
				       (dressStatusId !== 6 && dressStatusId !== 8); // Not authenticated or rejected
			case 'authenticated':
				// Check for authenticated: status = 'authenticated' OR dress_status_id = 6
				return status === 'authenticated' || status === 6 || dressStatusId === 6;
			case 'rejected':
				// Check for rejected: status = 'rejected' OR dress_status_id = 8
				return status === 'rejected' || status === 8 || dressStatusId === 8;
			default:
				return true;
		}
	};

	// Calculate counts for each tab
	const getTabCounts = () => {
		const counts = {
			pending: 0,
			authenticated: 0,
			rejected: 0
		};
		stockItems.forEach(item => {
			if (itemMatchesTab(item, 'pending')) counts.pending++;
			if (itemMatchesTab(item, 'authenticated')) counts.authenticated++;
			if (itemMatchesTab(item, 'rejected')) counts.rejected++;
		});
		return counts;
	};

	const tabCounts = getTabCounts();

	const filteredItems = stockItems.filter(item => {
		const searchLower = searchTerm.toLowerCase();
		const awbNumber = (item.awbNumber || item.awb_number || '').toLowerCase();
		const sellerName = (item.sellerName || item.seller_name || '').toLowerCase();
		const itemId = (item.id || '').toLowerCase();
		
		const matchesSearch = awbNumber.includes(searchLower) ||
			sellerName.includes(searchLower) ||
			itemId.includes(searchLower);
		
		const matchesTab = itemMatchesTab(item, activeTab);
		
		return matchesSearch && matchesTab;
	});

	const readOnlyAuth = !isAuthenticator();

	// Star Rating Component
	const StarRating = ({ param, label, value, onChange, readOnly = false }) => (
		<div style={{
			marginBottom: '0px'
		}}>
			<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
				<label style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
					{label}
				</label>
				<div style={{ fontSize: '12px', color: '#6b7280' }}>
					{value > 0 ? `${value}/5` : 'Not rated'}
				</div>
			</div>
			<div style={{ display: 'flex', gap: '4px' }}>
				{[1, 2, 3, 4, 5].map((star) => (
					<button
						key={star}
						type="button"
						onClick={!readOnly ? () => onChange(star) : undefined}
						style={{
							background: 'none',
							border: 'none',
							cursor: readOnly ? 'default' : 'pointer',
							padding: '2px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							opacity: readOnly ? 0.7 : 1
						}}
						disabled={readOnly}
					>
						<Star
							size={20}
							fill={star <= value ? '#fbbf24' : 'none'}
							color={star <= value ? '#fbbf24' : '#d1d5db'}
						/>
					</button>
				))}
			</div>
		</div>
	);

	return (
		<div className="page-container">
			<div className="page-header">
				<div>
					<h1 className="page-title">Branch Stock</h1>
					<p className="page-subtitle">Manage received items and their authentication status</p>
				</div>
				<button
					onClick={fetchBranchStock}
					className="btn btn-secondary"
					disabled={loading}
					title="Refresh Branch Stock"
					style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
				>
					<Package size={18} className={loading ? 'animate-spin' : ''} />
					Refresh
				</button>
			</div>

			{/* Tabs */}
			<div style={{
				display: 'flex',
				gap: '8px',
				marginBottom: '24px',
				borderBottom: '2px solid #e5e7eb'
			}}>
				<button
					onClick={() => setActiveTab('pending')}
					style={{
						padding: '12px 24px',
						border: 'none',
						background: 'transparent',
						borderBottom: activeTab === 'pending' ? '3px solid #6366f1' : '3px solid transparent',
						color: activeTab === 'pending' ? '#6366f1' : '#6b7280',
						fontSize: '14px',
						fontWeight: activeTab === 'pending' ? '600' : '500',
						cursor: 'pointer',
						transition: 'all 0.2s',
						marginBottom: '-2px',
						display: 'flex',
						alignItems: 'center',
						gap: '8px'
					}}
				>
					Pending
					{tabCounts.pending > 0 && (
						<span style={{
							backgroundColor: activeTab === 'pending' ? '#6366f1' : '#e5e7eb',
							color: activeTab === 'pending' ? 'white' : '#6b7280',
							padding: '2px 8px',
							borderRadius: '12px',
							fontSize: '12px',
							fontWeight: '600'
						}}>
							{tabCounts.pending}
						</span>
					)}
				</button>
				<button
					onClick={() => setActiveTab('authenticated')}
					style={{
						padding: '12px 24px',
						border: 'none',
						background: 'transparent',
						borderBottom: activeTab === 'authenticated' ? '3px solid #3b82f6' : '3px solid transparent',
						color: activeTab === 'authenticated' ? '#3b82f6' : '#6b7280',
						fontSize: '14px',
						fontWeight: activeTab === 'authenticated' ? '600' : '500',
						cursor: 'pointer',
						transition: 'all 0.2s',
						marginBottom: '-2px',
						display: 'flex',
						alignItems: 'center',
						gap: '8px'
					}}
				>
					Authenticated
					{tabCounts.authenticated > 0 && (
						<span style={{
							backgroundColor: activeTab === 'authenticated' ? '#3b82f6' : '#e5e7eb',
							color: activeTab === 'authenticated' ? 'white' : '#6b7280',
							padding: '2px 8px',
							borderRadius: '12px',
							fontSize: '12px',
							fontWeight: '600'
						}}>
							{tabCounts.authenticated}
						</span>
					)}
				</button>
				<button
					onClick={() => setActiveTab('rejected')}
					style={{
						padding: '12px 24px',
						border: 'none',
						background: 'transparent',
						borderBottom: activeTab === 'rejected' ? '3px solid #ef4444' : '3px solid transparent',
						color: activeTab === 'rejected' ? '#ef4444' : '#6b7280',
						fontSize: '14px',
						fontWeight: activeTab === 'rejected' ? '600' : '500',
						cursor: 'pointer',
						transition: 'all 0.2s',
						marginBottom: '-2px',
						display: 'flex',
						alignItems: 'center',
						gap: '8px'
					}}
				>
					Rejected
					{tabCounts.rejected > 0 && (
						<span style={{
							backgroundColor: activeTab === 'rejected' ? '#ef4444' : '#e5e7eb',
							color: activeTab === 'rejected' ? 'white' : '#6b7280',
							padding: '2px 8px',
							borderRadius: '12px',
							fontSize: '12px',
							fontWeight: '600'
						}}>
							{tabCounts.rejected}
						</span>
					)}
				</button>
			</div>

			{/* Filters */}
			<div style={{
				display: 'flex',
				gap: '16px',
				marginBottom: '24px',
				flexWrap: 'wrap'
			}}>
				{/* Search */}
				<div style={{ position: 'relative', width: '300px' }}>
					<Search
						size={18}
						style={{
							position: 'absolute',
							left: '12px',
							top: '50%',
							transform: 'translateY(-50%)',
							color: '#6b7280'
						}}
					/>
					<input
						type="text"
						placeholder="Search by AWB, seller, or ID..."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						className="search-box"
						style={{ paddingLeft: '40px', width: '100%', height: '44px' }}
					/>
				</div>
			</div>

			{/* Loading State */}
			{loading && (
				<div className="card">
					<div style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						padding: '40px',
						fontSize: '16px',
						color: '#6b7280'
					}}>
						<Package size={20} className="animate-spin" style={{ marginRight: '8px' }} />
						Loading branch stock...
					</div>
				</div>
			)}

			{/* Stock Items Table */}
			{!loading && (
			<>
			<div style={{ overflowX: 'auto' }}>
				<table className="table">
					<thead>
						<tr>
							<th>Stock ID</th>
							<th>Image</th>
							<th>AWB Number</th>
							<th>Seller</th>
							<th>Items</th>
							<th>Received Date</th>
							<th>Status</th>
							<th style={{ width: '100px' }}>Actions</th>
						</tr>
					</thead>
					<tbody>
						{filteredItems.map((item) => {
							const StatusIcon = getStatusIcon(item.status);
							// Get front image - prefer front_full, otherwise first image
							const getFrontImage = () => {
								if (!item.listingImages || item.listingImages.length === 0) {
									return null;
								}
								// Try to find front_full image first
								const frontImage = item.listingImages.find(img => 
									img.image_type === 'front_full' || img.image_type === 'front'
								);
								return frontImage || item.listingImages[0];
							};
							const frontImage = getFrontImage();
							return (
								<tr key={item.id}>
									<td>
										<div style={{ fontWeight: '600', color: '#111827' }}>
											{item.id}
										</div>
									</td>
									<td>
										{frontImage ? (
											<img
												src={frontImage.url || frontImage.thumbnail_url}
												alt="Listing image"
												style={{
													width: '60px',
													height: '60px',
													objectFit: 'cover',
													borderRadius: '4px',
													border: '1px solid #e5e7eb',
													cursor: 'pointer',
													backgroundColor: '#f3f4f6'
												}}
												onClick={() => openModal(item)}
												onError={(e) => {
													e.target.style.display = 'none';
												}}
												title="Click to view details"
											/>
										) : (
											<div style={{
												width: '60px',
												height: '60px',
												backgroundColor: '#f3f4f6',
												border: '1px solid #e5e7eb',
												borderRadius: '4px',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center'
											}}>
												<ImageIcon size={20} color="#9ca3af" />
											</div>
										)}
									</td>
									<td>
										<div style={{ fontFamily: 'monospace', fontSize: '14px' }}>
											{item.awbNumber || item.awb_number || 'N/A'}
										</div>
									</td>
									<td>
										<div>{item.seller_name || item.sellerName || 'Unknown'}</div>
										<div style={{ fontSize: '12px', color: '#6b7280' }}>
											{item.seller_email || item.sellerEmail || 'N/A'}
										</div>
									</td>
									<td>
										<span style={{
											backgroundColor: '#f3f4f6',
											color: '#374151',
											padding: '4px 8px',
											borderRadius: '12px',
											fontSize: '12px',
											fontWeight: '500'
										}}>
											{item.receivedCount}/{item.totalItems} items
										</span>
									</td>
									<td>
										<div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
											<Calendar size={12} color="#6b7280" />
											<span style={{ fontSize: '12px' }}>
												{new Date(item.receivedAt).toLocaleDateString()}
											</span>
										</div>
									</td>
									<td>
										<div style={{
											display: 'flex',
											alignItems: 'center',
											gap: '4px',
											backgroundColor: getStatusColor(item.status),
											color: 'white',
											padding: '4px 8px',
											borderRadius: '12px',
											fontSize: '12px',
											fontWeight: '500',
											width: 'fit-content'
										}}>
										<StatusIcon size={12} />
										{getStatusText(item.status)}
										</div>
									</td>
									<td>
										<button
											onClick={() => openModal(item)}
											className={isAuthenticator() ? "btn btn-primary" : "btn btn-secondary"}
											style={{
												fontSize: '11px',
												padding: '8px 12px',
												minWidth: 'auto',
												display: 'flex',
												alignItems: 'center',
												gap: '6px',
												...(isAuthenticator() && { backgroundColor: '#6366f1' })
											}}
											title={isAuthenticator() ? "Review Item" : "View Details"}
										>
											<Eye size={14} />
											{isAuthenticator() ? 'Review' : 'View'}
										</button>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			{filteredItems.length === 0 && (
				<div style={{
					textAlign: 'center',
					padding: '48px 24px',
					color: '#6b7280'
				}}>
					<Package size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
					<p>No stock items found matching your criteria</p>
				</div>
			)}
			</>
			)}

			{/* Stock Item Details Modal */}
			{showModal && selectedItem && (
				<div className="modal-overlay">
					<div className="modal-content" style={{ maxWidth: '1000px', width: '95%' }}>
						<div className="modal-header">
							<h2>Stock Item Details - {selectedItem.id}</h2>
							<button onClick={closeModal} className="modal-close">
								<X size={20} />
							</button>
						</div>

						<div className="modal-body" style={{ padding: '24px' }}>
							{/* Item Info Cards */}
							<div style={{
								display: 'grid',
								gridTemplateColumns: '1fr 1fr',
								gap: '24px',
								marginBottom: '32px'
							}}>
								<div style={{
									border: '1px solid #e5e7eb',
									borderRadius: '8px',
									padding: '16px',
									backgroundColor: '#f9fafb'
								}}>
									<h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
										Delivery Information
									</h3>
									<div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
										<div style={{ display: 'flex', justifyContent: 'space-between' }}>
											<span style={{ color: '#6b7280' }}>AWB Number:</span>
											<span style={{ fontWeight: '500', fontFamily: 'monospace' }}>{selectedItem.awbNumber || selectedItem.awb_number || 'N/A'}</span>
										</div>
										{!isAuthenticator() && (
											<>
												<div style={{ display: 'flex', justifyContent: 'space-between' }}>
													<span style={{ color: '#6b7280' }}>Seller:</span>
													<span style={{ fontWeight: '500' }}>{selectedItem.seller_name || selectedItem.sellerName || 'Unknown'}</span>
												</div>
												<div style={{ display: 'flex', justifyContent: 'space-between' }}>
													<span style={{ color: '#6b7280' }}>Email:</span>
													<span style={{ fontWeight: '500' }}>{selectedItem.seller_email || selectedItem.sellerEmail || 'N/A'}</span>
												</div>
											</>
										)}
										<div style={{ display: 'flex', justifyContent: 'space-between' }}>
											<span style={{ color: '#6b7280' }}>Purchase Method:</span>
											<span style={{
												fontWeight: '600',
												color: selectedItem.purchase_method === 'offer' ? '#f59e0b' : '#059669',
												padding: '2px 8px',
												borderRadius: '12px',
												backgroundColor: selectedItem.purchase_method === 'offer' ? '#fef3c7' : '#d1fae5',
												fontSize: '12px',
												textTransform: 'uppercase'
											}}>
												{selectedItem.purchase_method === 'offer' ? 'Offer' : 'Direct Purchase'}
											</span>
										</div>
										<div style={{ display: 'flex', justifyContent: 'space-between' }}>
											<span style={{ color: '#6b7280' }}>Asking Price:</span>
											<span style={{ fontWeight: '600', color: '#6b7280' }}>
												₹{selectedItem.asking_price ? selectedItem.asking_price.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'N/A'}
											</span>
										</div>
										<div style={{ display: 'flex', justifyContent: 'space-between' }}>
											<span style={{ color: '#6b7280' }}>Purchase Price:</span>
											<span style={{ fontWeight: '600', color: '#059669' }}>
												₹{selectedItem.total_amount ? selectedItem.total_amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
											</span>
										</div>
										<div style={{ display: 'flex', justifyContent: 'space-between' }}>
											<span style={{ color: '#6b7280' }}>Deposit Amount:</span>
											<span style={{ fontWeight: '600', color: '#7c3aed' }}>
												₹{selectedItem.deposit_amount ? selectedItem.deposit_amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
											</span>
										</div>
										{/* <div style={{ display: 'flex', justifyContent: 'space-between' }}>
											<span style={{ color: '#6b7280' }}>Seller Amount:</span>
											<span style={{ fontWeight: '600', color: '#2563eb' }}>
												₹{selectedItem.seller_amount ? selectedItem.seller_amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : (selectedItem.total_amount ? selectedItem.total_amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00')}
											</span>
										</div> */}
										<div style={{ display: 'flex', justifyContent: 'space-between' }}>
											<span style={{ color: '#6b7280' }}>Received Date:</span>
											<span style={{ fontWeight: '500' }}>{selectedItem.receivedAt ? new Date(selectedItem.receivedAt).toLocaleString() : 'N/A'}</span>
										</div>
										{(selectedItem.receivedBy || selectedItem.receivedByRole) && (
										<div style={{ display: 'flex', justifyContent: 'space-between' }}>
											<span style={{ color: '#6b7280' }}>Received By:</span>
											<span style={{ fontWeight: '500' }}>{selectedItem.receivedBy || 'Store'} {selectedItem.receivedByRole ? `(${selectedItem.receivedByRole})` : ''}</span>
										</div>
										)}
									</div>
								</div>

								<div style={{
									border: '1px solid #e5e7eb',
									borderRadius: '8px',
									padding: '16px',
									backgroundColor: '#ffffff'
								}}>
									<h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
										Status Information
									</h3>
									<div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
										<div style={{ display: 'flex', justifyContent: 'space-between' }}>
											<span style={{ color: '#6b7280' }}>Current Status:</span>
											<span style={{
												backgroundColor: getStatusColor(selectedItem.status),
												color: 'white',
												padding: '2px 8px',
												borderRadius: '12px',
												fontSize: '12px',
												fontWeight: '500'
											}}>
												{getStatusText(selectedItem.status)}
											</span>
										</div>
										{selectedItem.authenticatedBy && (
											<div style={{ display: 'flex', justifyContent: 'space-between' }}>
												<span style={{ color: '#6b7280' }}>Authenticated By:</span>
												<span style={{ fontWeight: '500' }}>{selectedItem.authenticatedBy}</span>
											</div>
										)}
										{selectedItem.authenticatedAt && (
											<div style={{ display: 'flex', justifyContent: 'space-between' }}>
												<span style={{ color: '#6b7280' }}>Authenticated At:</span>
												<span style={{ fontWeight: '500' }}>{new Date(selectedItem.authenticatedAt).toLocaleString()}</span>
											</div>
										)}
										<div style={{ display: 'flex', justifyContent: 'space-between' }}>
											<span style={{ color: '#6b7280' }}>Items Received:</span>
											<span style={{ fontWeight: '500' }}>{selectedItem.receivedCount}/{selectedItem.totalItems}</span>
										</div>
									</div>
								</div>
							</div>

							{/* Items List */}
							<div style={{ marginBottom: '24px' }}>
								<h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#111827' }}>
									Received Items ({selectedItem.totalItems} items)
								</h3>
								<div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
									<table style={{ width: '100%', borderCollapse: 'collapse' }}>
										<thead>
									<tr style={{ backgroundColor: '#f9fafb' }}>
												<th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Item</th>
												<th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Quantity</th>
												<th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Color</th>
												<th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Size</th>
												<th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#374151' }}>Status</th>
											</tr>
										</thead>
										<tbody>
									{selectedItem.items.map((item, index) => (
												<tr key={index} style={{ borderTop: '1px solid #e5e7eb' }}>
													<td style={{ padding: '12px', color: '#111827' }}>{item.name}</td>
													<td style={{ padding: '12px', textAlign: 'center', color: '#111827' }}>{item.quantity}</td>
													<td style={{ padding: '12px', color: '#111827' }}>{item.color}</td>
													<td style={{ padding: '12px', color: '#111827' }}>{item.size}</td>
													<td style={{ padding: '12px', textAlign: 'center' }}>
														<span style={{
															backgroundColor: item.received ? '#10b981' : '#f59e0b',
															color: 'white',
															padding: '2px 8px',
															borderRadius: '12px',
															fontSize: '12px',
															fontWeight: '500'
														}}>
															{item.received ? 'Received' : 'Missing'}
														</span>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							</div>

							{/* Images Section */}
							<div style={{ marginBottom: '24px' }}>
								<h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', color: '#111827' }}>
									Images
								</h3>

								{/* Listing Images (Dress Images) */}
								{selectedItem.listingImages && selectedItem.listingImages.length > 0 && (
									<div style={{ marginBottom: '24px' }}>
										<h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
											Listing Images ({selectedItem.listingImages.length})
										</h4>
										<div style={{
											display: 'grid',
											gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
											gap: '8px'
										}}>
											{selectedItem.listingImages.map((image, index) => (
												<div key={image.id || index} style={{ position: 'relative' }}>
													<img
														src={image.url || image.thumbnail_url}
														alt={image.alt || 'Listing image'}
														style={{
															width: '100%',
															height: '120px',
															objectFit: 'cover',
															borderRadius: '4px',
															border: '1px solid #e5e7eb',
															cursor: 'pointer'
														}}
														onClick={() => openImageModal(image, selectedItem.listingImages, index)}
														onError={(e) => {
															e.target.style.display = 'none';
														}}
													/>
													<div style={{
														position: 'absolute',
														bottom: '4px',
														left: '4px',
														right: '4px',
														backgroundColor: 'rgba(0,0,0,0.7)',
														color: 'white',
														padding: '2px 4px',
														borderRadius: '2px',
														fontSize: '10px',
														textAlign: 'center'
													}}>
														{image.image_type ? image.image_type.replace(/_/g, ' ') : ''}
													</div>
												</div>
											))}
										</div>
									</div>
								)}

								{/* Proof of Purchase Images */}
								{selectedItem.proofImages && selectedItem.proofImages.length > 0 && (
									<div style={{ marginBottom: '24px' }}>
										<h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
											Proof of Purchase ({selectedItem.proofImages.length})
										</h4>
										<div style={{
											display: 'grid',
											gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
											gap: '8px'
										}}>
											{selectedItem.proofImages.map((image, index) => (
												<div key={image.id || index} style={{ position: 'relative' }}>
													<img
														src={image.url}
														alt={image.alt || 'Proof of purchase'}
														style={{
															width: '100%',
															height: '120px',
															objectFit: 'cover',
															borderRadius: '4px',
															border: '1px solid #e5e7eb',
															cursor: 'pointer'
														}}
														onClick={() => openImageModal(image, selectedItem.proofImages, index)}
														onError={(e) => {
															e.target.style.display = 'none';
														}}
													/>
												</div>
											))}
										</div>
									</div>
								)}

								{/* Package Images (Shipping Images) */}
								{selectedItem.packageImages && selectedItem.packageImages.length > 0 && (
									<div style={{ marginBottom: '24px' }}>
										<h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
											Package/Shipping Images ({selectedItem.packageImages.length})
										</h4>
										<div style={{
											display: 'grid',
											gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
											gap: '8px'
										}}>
											{selectedItem.packageImages.map((image, index) => (
												<div key={image.id || index} style={{ position: 'relative' }}>
													<img
														src={image.url || image.preview}
														alt={image.alt || 'Package image'}
														style={{
															width: '100%',
															height: '120px',
															objectFit: 'cover',
															borderRadius: '4px',
															border: '1px solid #e5e7eb',
															cursor: 'pointer'
														}}
														onClick={() => openImageModal(image, selectedItem.packageImages, index)}
														onError={(e) => {
															e.target.style.display = 'none';
														}}
													/>
												</div>
											))}
										</div>
									</div>
								)}

								{/* Received Item Images */}
								{selectedItem.receivedItemImages && selectedItem.receivedItemImages.length > 0 && (
									<div style={{ marginBottom: '24px' }}>
										<h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
											Received Item Images ({selectedItem.receivedItemImages.length})
										</h4>
										<div style={{
											display: 'grid',
											gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
											gap: '8px'
										}}>
											{selectedItem.receivedItemImages.map((image, index) => (
												<div key={image.id || index} style={{ position: 'relative' }}>
													<img
														src={image.url || image.preview}
														alt={image.alt || 'Received item image'}
														style={{
															width: '100%',
															height: '120px',
															objectFit: 'cover',
															borderRadius: '4px',
															border: '1px solid #e5e7eb',
															cursor: 'pointer'
														}}
														onClick={() => openImageModal(image, selectedItem.receivedItemImages, index)}
														onError={(e) => {
															e.target.style.display = 'none';
														}}
													/>
												</div>
											))}
										</div>
									</div>
								)}

								{/* Show message if no images */}
								{(!selectedItem.listingImages || selectedItem.listingImages.length === 0) &&
								 (!selectedItem.proofImages || selectedItem.proofImages.length === 0) &&
								 (!selectedItem.packageImages || selectedItem.packageImages.length === 0) &&
								 (!selectedItem.receivedItemImages || selectedItem.receivedItemImages.length === 0) && (
									<div style={{
										textAlign: 'center',
										padding: '24px',
										backgroundColor: '#f9fafb',
										borderRadius: '8px',
										color: '#6b7280'
									}}>
										<ImageIcon size={32} style={{ margin: '0 auto 8px', opacity: 0.5 }} />
										<p>No images available for this item</p>
									</div>
								)}
							</div>

							{/* Remarks */}
							{selectedItem.remarks && (
								<div style={{ marginBottom: '24px' }}>
									<h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
										Remarks
									</h3>
									<div style={{
										backgroundColor: '#f9fafb',
										border: '1px solid #e5e7eb',
										borderRadius: '8px',
										padding: '12px',
										fontSize: '14px',
										color: '#374151'
									}}>
										{selectedItem.remarks}
									</div>
								</div>
							)}

							{/* Authentication Section */}
							<div style={{ marginBottom: '24px' }}>
								{/* Authentication Assessment Card */}
								<div style={{
									backgroundColor: 'white',
									border: '1px solid #e5e7eb',
									borderRadius: '8px',
									padding: '20px',
									marginBottom: '16px',
									boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
								}}>
									<h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
										Authentication Assessment
									</h3>
									{readOnlyAuth && (
										<div style={{
											backgroundColor: '#f9fafb',
											border: '1px solid #e5e7eb',
											borderRadius: '6px',
											padding: '10px 12px',
											fontSize: '13px',
											color: '#6b7280',
											marginBottom: '12px'
										}}>
											View-only mode. Store managers and admins can review these ratings but only authenticators can modify them.
										</div>
									)}

									<div style={{
										display: 'flex',
										flexDirection: 'column',
										gap: '16px'
									}}>
										<StarRating
											param="purchaseProof"
											label="Purchase Proof"
											value={authParams.purchaseProof}
											onChange={(rating) => handleStarClick('purchaseProof', rating)}
											readOnly={readOnlyAuth}
										/>

										<StarRating
											param="fabric"
											label="Fabric"
											value={authParams.fabric}
											onChange={(rating) => handleStarClick('fabric', rating)}
											readOnly={readOnlyAuth}
										/>

										<StarRating
											param="stitching"
											label="Stitching"
											value={authParams.stitching}
											onChange={(rating) => handleStarClick('stitching', rating)}
											readOnly={readOnlyAuth}
										/>

										<StarRating
											param="labels"
											label="Labels"
											value={authParams.labels}
											onChange={(rating) => handleStarClick('labels', rating)}
											readOnly={readOnlyAuth}
										/>

										<StarRating
											param="condition"
											label="Condition"
											value={authParams.condition}
											onChange={(rating) => handleStarClick('condition', rating)}
											readOnly={readOnlyAuth}
										/>
									</div>
								</div>

								{/* Average Condition Card */}
								<div style={{
									backgroundColor: 'white',
									border: '1px solid #e5e7eb',
									borderRadius: '8px',
									padding: '20px',
									marginBottom: '16px',
									boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
									textAlign: 'center'
								}}>
									<h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
										Average Condition
									</h3>
									<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
										<Star size={24} fill="#fbbf24" color="#fbbf24" />
										<span style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827' }}>
											{getAverageRating()}
										</span>
										<span style={{ fontSize: '14px', color: '#6b7280' }}>/ 5.0</span>
									</div>
								</div>

								{/* Overall Remark Card */}
								<div style={{
									backgroundColor: 'white',
									border: '1px solid #e5e7eb',
									borderRadius: '8px',
									padding: '20px',
									boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
								}}>
									<h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#111827' }}>
										Overall Remark
									</h3>
									
									{!readOnlyAuth ? (
										<>
											{/* Predefined Remarks */}
											<div style={{ marginBottom: '12px' }}>
												<div style={{ 
													fontSize: '12px', 
													color: '#6b7280', 
													marginBottom: '8px',
													fontWeight: '500'
												}}>
													Quick Select:
												</div>
												<div style={{
													display: 'flex',
													flexWrap: 'wrap',
													gap: '8px'
												}}>
													{[
														'Authenticated product',
														'Fake product',
														'Good condition',
														'Poor condition',
														'As described',
														'Needs repair',
														'Excellent quality',
														'Minor defects',
														'Original tags present',
														'No tags found'
													].map((remark) => (
														<button
															key={remark}
															type="button"
															onClick={() => {
																if (overallRemark) {
																	let trimmed = overallRemark.trim();
																	if (trimmed.endsWith('.')) {
																		trimmed = trimmed.slice(0, -1);
																	}
																	setOverallRemark(trimmed + ', ' + remark + '.');
																} else {
																	setOverallRemark(remark + '.');
																}
															}}
															style={{
																padding: '6px 12px',
																border: '1px solid #d1d5db',
																borderRadius: '6px',
																backgroundColor: '#f9fafb',
																color: '#374151',
																fontSize: '12px',
																cursor: 'pointer',
																transition: 'all 0.2s',
																fontWeight: '500'
															}}
															onMouseEnter={(e) => {
																e.target.style.backgroundColor = '#f3f4f6';
																e.target.style.borderColor = '#9ca3af';
															}}
															onMouseLeave={(e) => {
																e.target.style.backgroundColor = '#f9fafb';
																e.target.style.borderColor = '#d1d5db';
															}}
														>
															{remark}
														</button>
													))}
												</div>
											</div>
											
											<textarea
												value={overallRemark}
												onChange={(e) => setOverallRemark(e.target.value)}
												rows={4}
												style={{
													width: '100%',
													border: '1px solid #d1d5db',
													borderRadius: '8px',
													padding: '12px',
													outline: 'none',
													fontSize: '14px',
													resize: 'vertical'
												}}
												placeholder="Add detailed remarks about the authentication assessment..."
											/>
											<div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
												<label style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>
													Overall Estimate (₹)
													<span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>
												</label>
												<input
													type="number"
													min="0"
													step="0.01"
													value={overallEstimate}
													onChange={(e) => setOverallEstimate(e.target.value)}
													placeholder="0.00"
													required
													style={{
														width: '200px',
														border: '1px solid #d1d5db',
														borderRadius: '6px',
														padding: '8px 10px',
														fontSize: '14px'
													}}
												/>
											</div>
										</>
									) : (
										<>
											<div style={{
												backgroundColor: '#f9fafb',
												border: '1px solid #e5e7eb',
												borderRadius: '8px',
												padding: '12px',
												fontSize: '14px',
												color: '#374151',
												minHeight: '80px'
											}}>
												{overallRemark ? overallRemark : 'No remarks have been added yet.'}
											</div>
											<div style={{
												marginTop: '12px',
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'center',
												padding: '12px',
												border: '1px solid #e5e7eb',
												borderRadius: '8px',
												backgroundColor: '#f9fafb',
												fontSize: '14px',
												color: '#111827'
											}}>
												<span style={{ fontWeight: '600' }}>Overall Estimate (₹):</span>
												<span style={{ fontWeight: '700', color: '#059669' }}>
													{overallEstimate
														? `₹${Number(overallEstimate).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
														: 'Not provided'}
												</span>
											</div>
										</>
									)}
								</div>
							</div>

							{/* Status Actions */}
							<div style={{
								display: 'flex',
								gap: '12px',
								justifyContent: 'flex-end',
								paddingTop: '16px',
								borderTop: '1px solid #e5e7eb'
							}}>
				{isStatusPendingOrInProcess(selectedItem.status) && isAuthenticator() && (
									<>
										<button
											onClick={handleMarkAuthenticated}
											className="btn"
											style={{
												backgroundColor: '#3b82f6',
												color: 'white',
												border: '1px solid #3b82f6'
											}}
										>
											Mark as Authenticated
										</button>
										<button
											onClick={handleReject}
											className="btn"
											style={{
												backgroundColor: '#ef4444',
												color: 'white',
												border: '1px solid #ef4444'
											}}
										>
											Reject
										</button>
									</>
								)}
								{/* {selectedItem.status === 'authenticated' && user?.role === 'authenticator' && (
									<>
										<button
											onClick={() => updateStatus(selectedItem.id, 'approved')}
											className="btn"
											style={{
												backgroundColor: '#10b981',
												color: 'white',
												border: '1px solid #10b981'
											}}
										>
											Approve
										</button>
										<button
											onClick={() => updateStatus(selectedItem.id, 'rejected')}
											className="btn"
											style={{
												backgroundColor: '#ef4444',
												color: 'white',
												border: '1px solid #ef4444'
											}}
										>
											Reject
										</button>
									</>
								)} */}
								{!isAuthenticator() && (
									<div style={{
										padding: '8px 16px',
										backgroundColor: '#f3f4f6',
										color: '#6b7280',
										borderRadius: '6px',
										fontSize: '14px',
										fontStyle: 'italic'
									}}>
										Only Authenticator role can update status
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Full Screen Image Modal */}
			{fullScreenImage && (
				<div style={{
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: 'rgba(0, 0, 0, 0.9)',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					zIndex: 2000,
					padding: '20px'
				}}>
					<div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
						<img
							src={currentImageArray[currentImageIndex]?.url || fullScreenImage.url}
							alt={currentImageArray[currentImageIndex]?.alt || fullScreenImage.alt}
							style={{
								maxWidth: '100%',
								maxHeight: '100%',
								objectFit: 'contain'
							}}
						/>

						{/* Navigation Arrows */}
						{currentImageArray.length > 1 && (
							<>
								<button
									onClick={() => navigateImage('prev')}
									style={{
										position: 'absolute',
										left: '20px',
										top: '50%',
										transform: 'translateY(-50%)',
										background: 'rgba(0, 0, 0, 0.5)',
										color: 'white',
										border: 'none',
										borderRadius: '50%',
										width: '50px',
										height: '50px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										cursor: 'pointer'
									}}
								>
									<ChevronLeft size={24} />
								</button>
								<button
									onClick={() => navigateImage('next')}
									style={{
										position: 'absolute',
										right: '20px',
										top: '50%',
										transform: 'translateY(-50%)',
										background: 'rgba(0, 0, 0, 0.5)',
										color: 'white',
										border: 'none',
										borderRadius: '50%',
										width: '50px',
										height: '50px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										cursor: 'pointer'
									}}
								>
									<ChevronRight size={24} />
								</button>
							</>
						)}

						{/* Close Button */}
						<button
							onClick={closeImageModal}
							style={{
								position: 'absolute',
								top: '20px',
								right: '20px',
								background: 'rgba(0, 0, 0, 0.5)',
								color: 'white',
								border: 'none',
								borderRadius: '50%',
								width: '40px',
								height: '40px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								cursor: 'pointer'
							}}
						>
							<X size={20} />
						</button>

						{/* Image Counter */}
						{currentImageArray.length > 1 && (
							<div style={{
								position: 'absolute',
								bottom: '20px',
								left: '50%',
								transform: 'translateX(-50%)',
								background: 'rgba(0, 0, 0, 0.5)',
								color: 'white',
								padding: '8px 16px',
								borderRadius: '20px',
								fontSize: '14px'
							}}>
								{currentImageIndex + 1} / {currentImageArray.length}
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default BranchStock;